import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../apiconfig/api-config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { FiLogOut } from 'react-icons/fi';
import { formatDateDDMMYY } from '../common/dateUtils';

const Profile = ({ onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [updating, setUpdating] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch profile data on component mount
  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/profile/profile-get`,
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setProfile(response.data.data);
        setFormData({
          name: response.data.data.name,
          email: response.data.data.email
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error(
        error.response?.data?.error || 'Failed to load profile',
        {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({
      name: profile.name,
      email: profile.email
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Name and email are required', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
      return;
    }

    try {
      setUpdating(true);
      const response = await axios.put(
        `${API_BASE_URL}/api/profile/profile-update`,
        formData,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success('Profile updated successfully!', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        });
        
        // Refresh profile data
        await fetchProfile();
        setEditing(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(
        error.response?.data?.error || 'Failed to update profile',
        {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        }
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      
      console.log('Starting logout process...');
      
      // Call logout API to clear server-side tokens
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );

      console.log('Logout API response:', response.data);

      // Clear any client-side stored tokens
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      
      // Clear axios default headers
      delete axios.defaults.headers.common['Authorization'];
      
      toast.success('Logged out successfully!', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });

      // Close profile panel
      if (onClose) {
        onClose();
      }

      // Redirect to login page
      setTimeout(() => {
        navigate('/');
      }, 1000);

    } catch (error) {
      console.error('Logout error:', error);
      console.error('Error response:', error.response?.data);
      
      // Even if server logout fails, clear client-side tokens
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      
      toast.error('Logout failed, but tokens cleared locally', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });

      // Still redirect to login
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } finally {
      setLogoutLoading(false);
    }
  };

  // Using the common formatDateDDMMYY function from dateUtils

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#15803d] mx-auto"></div>
          <p className="mt-1 text-[10px] text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="text-center">
          <p className="text-[10px] text-gray-500">Profile not found</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer 
        toastClassName={() =>
          'relative flex items-center p-3 min-h-10 rounded-md justify-between overflow-hidden cursor-pointer bg-[#d97706]/90 text-white text-[13px] font-semibold shadow-lg backdrop-blur-sm border border-[#b45309]/30'
        }
      />
      
      <div className="space-y-5">
        {editing ? (
          // Edit Form
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-4">
              <div>
                <label className="block text-[13px] font-semibold text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 text-[14px] border border-gray-300 rounded focus:outline-none focus:border-[#d97706] focus:ring-2 focus:ring-[#d97706]"
                  required
                />
              </div>
              
              <div>
                <label className="block text-[13px] font-semibold text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 text-[14px] border border-gray-300 rounded focus:outline-none focus:border-[#d97706] focus:ring-2 focus:ring-[#d97706]"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1.5 text-[14px] border border-gray-300 text-gray-700 rounded hover:bg-gray-100 font-medium transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updating}
                className={`px-3 py-1.5 text-[14px] text-white rounded font-semibold transition-colors duration-200 ${
                  updating 
                    ? 'bg-[#d97706] opacity-70 cursor-not-allowed' 
                    : 'bg-[#d97706] hover:bg-[#b45309]'
                }`}
              >
                {updating ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        ) : (
          // Display Profile
          <div className="space-y-5">
            {/* Modern Profile Header */}
            <div className="flex flex-col items-center pb-2 relative">
              <button
                onClick={handleEdit}
                className="absolute top-0 right-0 text-[12px] font-semibold text-amber-600 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-md transition-colors duration-200"
              >
                Edit Profile
              </button>
              
              <div className="w-16 h-16 rounded-full bg-green-100 border-2 border-green-200 flex items-center justify-center mb-3 shadow-sm">
                <span className="text-3xl font-bold text-[#15803d]">
                  {profile.name ? profile.name.charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
              
              <h4 className="text-[15px] font-bold text-gray-900">{profile.name}</h4>
              <p className="text-[13px] text-gray-500 mt-0.5">{profile.email}</p>
              
              <div className="flex items-center gap-3 mt-3">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-bold bg-amber-100 text-amber-700 shadow-sm border border-amber-200">
                  {profile.role?.toUpperCase()}
                </span>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-bold shadow-sm border ${
                  profile.isActive 
                    ? 'bg-green-50 text-green-700 border-green-200' 
                    : 'bg-red-50 text-red-700 border-red-200'
                }`}>
                  {profile.isActive ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-3 mt-2">
              <h4 className="text-[12px] font-bold text-gray-400 uppercase tracking-wider mb-3">Account Details</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center bg-gray-50 px-3 py-1.5 rounded-md">
                  <span className="text-[12px] font-medium text-gray-500">Last Login</span>
                  <span className="text-[12px] font-semibold text-gray-700">{formatDateDDMMYY(profile.lastLogin)}</span>
                </div>
                <div className="flex justify-between items-center bg-gray-50 px-3 py-1.5 rounded-md">
                  <span className="text-[12px] font-medium text-gray-500">Created</span>
                  <span className="text-[12px] font-semibold text-gray-700">{formatDateDDMMYY(profile.createdAt)}</span>
                </div>
                <div className="flex justify-between items-center bg-gray-50 px-3 py-1.5 rounded-md">
                  <span className="text-[12px] font-medium text-gray-500">Updated</span>
                  <span className="text-[12px] font-semibold text-gray-700">{formatDateDDMMYY(profile.updatedAt)}</span>
                </div>
              </div>
            </div>

            {/* Logout Button */}
            <div className="pt-2">
              <button
                onClick={handleLogout}
                disabled={logoutLoading}
                className={`w-full flex items-center justify-center gap-2 px-3 py-1.5.5 text-[13px] font-bold rounded-lg transition-all duration-200 ${
                  logoutLoading 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 border border-red-100'
                }`}
              >
                <FiLogOut className="text-[15px]" />
                {logoutLoading ? 'Logging out...' : 'Log Out'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Profile;
